import { useCallback, useEffect, useState } from "react";

import { toast } from "sonner";

import { useApi } from "../../../context/ApiContext";
import { useConfirm } from "../../../context/ConfirmContext.jsx";
import { usePagination } from "../../../hooks/usePagination";

export const useProjectApi = (initialPageSize = 10) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("createdAt");
  const [direction, setDirection] = useState("desc");
  const [showDeleted, setShowDeleted] = useState(false);
  const pagination = usePagination(initialPageSize);
  const { currentPage: page, pageSize, setPaginationData, items: projects, setItems: setProjects } = pagination;
  const [isLoading, setIsLoading] = useState(false);
  const api = useApi();

  const fetchProjects = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await api.get(`/projects`, {
        params: {
          page,
          size: pageSize,
          search: searchTerm?.trim() || undefined,
          status: statusFilter !== "ALL" ? statusFilter : undefined,
          sortBy: sortBy || undefined,
          direction: direction || undefined,
          showDeleted: showDeleted
        }
      });
      setPaginationData(response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch projects:", error);
      toast.error(error.message || "Failed to fetch projects");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [api, page, pageSize, searchTerm, statusFilter, sortBy, direction, showDeleted, setPaginationData]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const confirm = useConfirm();

  const deleteProject = async (projectId) => {
    const isConfirmed = await confirm({
      title: "Delete Project",
      message:
        "Are you sure you want to delete this project? This action cannot be undone and will delete all associated data including milestones, tasks, activities, and materials."
    });
    if (isConfirmed) {
      try {
        await api.delete(`/projects/${projectId}`);
        // Refresh to ensure pagination is correct after deletion
        fetchProjects();
        toast.success("Project deleted successfully!");
        return true;
      } catch (error) {
        toast.error(error.message || "Failed to delete project");
        console.error("Delete project error:", error);
        return false;
      }
    }
    return false;
  };

  const createProject = async (projectData) => {
    try {
      await api.post(`/projects`, projectData);
      // Refresh to show new project and update pagination
      fetchProjects();
      toast.success("Project created successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to create project");
      throw error;
    }
  };

  const updateProject = async (projectId, updatedData) => {
    try {
      const response = await api.put(`/projects/${projectId}`, updatedData);
      setProjects((prev) => prev.map((p) => (p.id === projectId ? response.data : p)));
      toast.success("Project updated successfully!");
      return response.data;
    } catch (error) {
      toast.error(error.message || "Failed to update project");
      throw error;
    }
  };

  const duplicateProject = async (projectId, duplicateData) => {
    try {
      await api.post(`/projects/${projectId}/duplicate`, duplicateData);
      // Refresh to show duplicated project and update pagination
      fetchProjects();
      toast.success("Project duplicated successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to duplicate project");
      throw error;
    }
  };

  const createDefaultMilestones = async (projectId) => {
    const defaultMilestones = ["Site Preparation"];
    try {
      for (const milestoneName of defaultMilestones) {
        await api.post(`/milestones`, {
          projectId: projectId,
          name: milestoneName,
          description: `${milestoneName} phase of construction`
        });
      }
      toast.success("Default milestones created!");
    } catch (error) {
      toast.error(error.message || "Failed to create milestones");
    }
  };

  return {
    projects,
    pagination,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    sortBy,
    setSortBy,
    direction,
    setDirection,
    showDeleted,
    setShowDeleted,
    isLoading,
    fetchProjects,
    deleteProject,
    createProject,
    updateProject,
    duplicateProject,
    createDefaultMilestones
  };
};
